import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { memo, useEffect, useRef, useState } from "react";
import { AUDIO_RENDITION_NAME, ContentNature, ContentObjectStatus, ImageRenditionFormat, MarkdownRenditionFormat, PDF_RENDITION_NAME, Permission, POSTER_RENDITION_NAME, WorkflowExecutionStatus } from "@vertesia/common";
import { Button, Dropdown, MenuItem, Portal, ResizableHandle, ResizablePanel, ResizablePanelGroup, Spinner, useFetch, useToast } from "@vertesia/ui/core";
import { NavLink } from "@vertesia/ui/router";
import { useUserSession } from "@vertesia/ui/session";
import { JSONDisplay, MarkdownRenderer, Progress, XMLViewer } from "@vertesia/ui/widgets";
import { AlertTriangle, Copy, Download, FileSearch, SquarePen } from "lucide-react";
import { useUITranslation } from '../../../../i18n/index.js';
import { MagicPdfView } from "../../../magic-pdf";
import { SimplePdfViewer } from "../../../pdf-viewer";
import { SecureButton } from "../../../permissions/SecureButton.js";
import { getWorkflowStatusColor, getWorkflowStatusName, isPreviewableAsPdf } from "../../../utils/index.js";
import { PropertiesEditorModal } from "./PropertiesEditorModal";
import { TextEditorPanel } from "./TextEditorPanel.js";
import { useObjectText, useOfficePdfConversion, usePdfProcessingStatus } from "./useContentPanelHooks.js";
import { useDownloadFile } from "./useDownloadFile.js";
// Web-supported image formats for browser display
const WEB_SUPPORTED_IMAGE_FORMATS = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
// Web-supported video formats for browser display
const WEB_SUPPORTED_VIDEO_FORMATS = ['video/mp4', 'video/webm'];
// Web-supported audio formats for browser display
const WEB_SUPPORTED_AUDIO_FORMATS = [
    'audio/mp4', // M4A/AAC (official MIME type)
    'audio/m4a', // M4A (alternative MIME type)
    'audio/x-m4a', // M4A (legacy MIME type)
    'audio/mpeg', // MP3
    'audio/ogg', // Ogg Vorbis
    'audio/wav', // WAV
    'audio/webm', // WebM audio
];
/**
 * Custom markdown components for the content overview.
 * Handles internal links to store objects and provides consistent styling.
 */
const createMarkdownComponents = () => ({
    a: ({ node, ...props }) => {
        const href = props.href || "";
        if (href.includes("/store/objects/")) {
            return (_jsx(NavLink, { topLevelNav: true, href: href, className: "text-info", children: props.children }));
        }
        return _jsx("a", { ...props, target: "_blank", rel: "noopener noreferrer" });
    },
    p: ({ node, ...props }) => (_jsx("p", { ...props, className: "my-0" })),
    pre: ({ node, ...props }) => (_jsx("pre", { ...props, className: "my-2 p-2 rounded" })),
    code: ({ node, className, children, ...props }) => {
        const match = /language-(\w+)/.exec(className || "");
        const isInline = !match;
        return (_jsx("code", { ...props, className: isInline ? "px-1.5 py-0.5 rounded" : "text-muted", children: children }));
    },
    h1: ({ node, ...props }) => (_jsx("h1", { ...props, className: "font-bold text-2xl my-2" })),
    h2: ({ node, ...props }) => (_jsx("h2", { ...props, className: "font-bold text-xl my-2" })),
    h3: ({ node, ...props }) => (_jsx("h3", { ...props, className: "font-bold text-lg my-2" })),
    li: ({ node, ...props }) => _jsx("li", { ...props }),
});
/**
 * Check if an object is in created or processing status.
 */
function isCreatedOrProcessingStatus(status) {
    return status === ContentObjectStatus.created || status === ContentObjectStatus.processing;
}
/**
 * Get the content processor type from object metadata.
 */
function getContentProcessorType(object) {
    return object.metadata?.content_processor?.type;
}
/**
 * Check if text content appears to be markdown based on common patterns.
 */
function looksLikeMarkdown(text) {
    if (!text)
        return false;
    return (text.includes("\n# ") ||
        text.includes("\n## ") ||
        text.includes("\n### ") ||
        text.includes("\n* ") ||
        text.includes("\n- ") ||
        text.includes("\n+ ") ||
        text.includes("![") ||
        text.includes("]("));
}
/**
 * Helper function to get panel visibility className.
 * Returns empty string if visible, 'hidden' if not visible.
 */
function getPanelVisibility(isVisible) {
    return isVisible ? 'h-full overflow-auto' : 'hidden';
}
var PanelView;
(function (PanelView) {
    PanelView["Text"] = "text";
    PanelView["Image"] = "image";
    PanelView["Video"] = "video";
    PanelView["Audio"] = "audio";
    PanelView["Pdf"] = "pdf";
    PanelView["Transcript"] = "transcript";
})(PanelView || (PanelView = {}));
export function ContentOverview({ object, loadText, refetch, }) {
    const toast = useToast();
    const { t } = useUITranslation();
    const handleCopyContent = async (content, type) => {
        try {
            await navigator.clipboard.writeText(content);
            toast({
                status: "success",
                title: t('store.contentCopied', { type: type === "text" ? t('store.contentType') : t('store.properties') }),
                description: t('store.successfullyCopied', { type }),
                duration: 2000,
            });
        }
        catch (err) {
            console.error(`Failed to copy ${type}:`, err);
            toast({
                status: "error",
                title: t('store.copyFailed'),
                description: t('store.failedToCopy', { type }),
                duration: 5000,
            });
        }
    };
    return (_jsx(_Fragment, { children: _jsxs(ResizablePanelGroup, { direction: "horizontal", className: 'h-full', children: [_jsx(ResizablePanel, { className: "min-w-[100px]", children: _jsx(PropertiesPanel, { object: object, refetch: refetch ?? (() => Promise.resolve()), handleCopyContent: handleCopyContent }) }), _jsx(ResizableHandle, { withHandle: true }), _jsx(ResizablePanel, { className: "min-w-[100px]", children: _jsx(DataPanel, { object: object, loadText: loadText ?? false, handleCopyContent: handleCopyContent, refetch: refetch }) })] }) }));
}
function PropertiesPanel({ object, refetch, handleCopyContent }) {
    const { t } = useUITranslation();
    const [viewCode, setViewCode] = useState(false);
    const [isPropertiesModalOpen, setPropertiesModalOpen] = useState(false);
    const handleOpenPropertiesModal = () => {
        setPropertiesModalOpen(true);
    };
    const handleClosePropertiesModal = () => {
        setPropertiesModalOpen(false);
    };
    return (_jsxs(_Fragment, { children: [_jsxs("div", { className: "flex flex-col h-full", children: [_jsxs("div", { className: "flex justify-between items-center px-2", children: [_jsxs("div", { className: "flex items-center gap-1 bg-muted mb-2 p-1 rounded", children: [_jsx(Button, { variant: `${viewCode ? "ghost" : "primary"}`, size: "sm", alt: t('store.previewProperties'), onClick: () => setViewCode(!viewCode), children: "Properties" }), _jsx(Button, { variant: `${viewCode ? "primary" : "ghost"}`, size: "sm", alt: t('store.viewInJsonFormat'), onClick: () => setViewCode(!viewCode), children: "JSON" })] }), _jsxs("div", { className: "flex items-center gap-2", children: [object.properties && (_jsx(Button, { variant: "ghost", size: "sm", title: "Copy properties", onClick: () => handleCopyContent(JSON.stringify(object.properties, null, 2), "properties"), children: _jsx(Copy, { className: "size-4" }) })), _jsx(Button, { variant: "ghost", size: "sm", onClick: handleOpenPropertiesModal, title: "Edit properties", className: "flex items-center gap-2", children: _jsx(SquarePen, { className: "size-4" }) })] })] }), object.properties ? (_jsx("div", { className: "flex-1 min-h-0 px-2", children: _jsx(JSONDisplay, { value: object.properties, viewCode: viewCode }) })) : (_jsx("div", { className: "flex-1 min-h-0 px-2", children: _jsx("div", { children: t('store.noPropertiesDefined') }) }))] }), _jsx(PropertiesEditorModal, { isOpen: isPropertiesModalOpen, onClose: handleClosePropertiesModal, object: object, refetch: refetch })] }));
}
function DataPanel({ object, loadText, handleCopyContent, refetch }) {
    const { t } = useUITranslation();
    const isImage = object?.metadata?.type === ContentNature.Image;
    const isVideo = object?.metadata?.type === ContentNature.Video;
    const isAudio = object?.metadata?.type === ContentNature.Audio;
    const isPdf = object?.content?.type === 'application/pdf';
    const isPreviewableAsPdfDoc = object?.content?.type ? isPreviewableAsPdf(object.content.type) : false;
    const isCreatedOrProcessing = isCreatedOrProcessingStatus(object?.status);
    const hasTranscript = !!(object.transcript && (isVideo || isAudio));
    // Check if PDF rendition exists for Office documents
    const metadata = object.metadata;
    const pdfRendition = metadata?.renditions?.find(r => r.name === PDF_RENDITION_NAME);
    // Determine initial panel view
    const getInitialView = () => {
        if (isVideo)
            return PanelView.Video;
        if (isAudio)
            return PanelView.Audio;
        if (isImage)
            return PanelView.Image;
        return PanelView.Text;
    };
    const [currentPanel, setCurrentPanel] = useState(getInitialView());
    const [hasVisitedPdfPanel, setHasVisitedPdfPanel] = useState(currentPanel === PanelView.Pdf);
    useEffect(() => {
        if (currentPanel === PanelView.Pdf) {
            setHasVisitedPdfPanel(true);
        }
    }, [currentPanel]);
    // Text editing state
    const [isEditing, setIsEditing] = useState(false);
    const canEdit = !!(object.content?.source &&
        object.content?.type &&
        !isCreatedOrProcessing &&
        !object.is_locked &&
        object.user_permissions?.can_write !== false &&
        (object.content.type.startsWith('text/') || object.content.type === 'application/json' || object.content.type === 'application/xml'));
    // Use custom hooks for text loading, PDF processing, and Office conversion
    const { fullText, displayText, isLoading: isLoadingText, isCropped: isTextCropped, loadText: reloadText, } = useObjectText(object.id, object.text, loadText);
    // Only poll while the active panel can actually surface processing progress.
    const shouldPollProgress = (isPdf || isPreviewableAsPdfDoc)
        && isCreatedOrProcessing
        && (currentPanel === PanelView.Text || currentPanel === PanelView.Pdf);
    const { progress: pdfProgress, status: pdfStatus, outputFormat: pdfOutputFormat, isComplete: processingComplete, } = usePdfProcessingStatus(object.id, shouldPollProgress);
    // Office document PDF conversion
    const { pdfUrl: officePdfUrl, isConverting: officePdfConverting, error: officePdfError, triggerConversion: triggerOfficePdfConversion, } = useOfficePdfConversion(object.id, isPreviewableAsPdfDoc);
    // Load text once processing completes without triggering a full object refetch
    // (which would flash the page-level loading spinner).
    useEffect(() => {
        if (processingComplete && pdfStatus === WorkflowExecutionStatus.COMPLETED) {
            reloadText();
        }
    }, [processingComplete, pdfStatus, reloadText]);
    // Show processing panel when workflow is running (for both PDFs and Office documents)
    const showProcessingPanel = (isPdf || isPreviewableAsPdfDoc) && isCreatedOrProcessing && !processingComplete && pdfStatus === WorkflowExecutionStatus.RUNNING;
    const showPdfPreviewPanel = currentPanel === PanelView.Pdf && !showProcessingPanel;
    const showPdfProcessingPanel = showProcessingPanel && (currentPanel === PanelView.Text || currentPanel === PanelView.Pdf);
    const keepPdfPreviewMounted = hasVisitedPdfPanel && !showProcessingPanel;
    const textContainerRef = useRef(null);
    return (_jsxs("div", { className: "flex flex-col h-full", children: [_jsxs("div", { className: "flex justify-between items-center px-2 shrink-0", children: [_jsxs("div", { className: "flex items-center gap-2 mb-2", children: [_jsxs("div", { className: "flex items-center gap-1 bg-muted p-1 rounded", children: [isImage &&
                                        _jsx(Button, { variant: currentPanel === PanelView.Image ? "primary" : "ghost", size: "sm", alt: t('store.viewImage'), onClick: () => setCurrentPanel(PanelView.Image), children: "Image" }), isVideo &&
                                        _jsx(Button, { variant: currentPanel === PanelView.Video ? "primary" : "ghost", size: "sm", alt: t('store.viewVideo'), onClick: () => setCurrentPanel(PanelView.Video), children: "Video" }), isAudio &&
                                        _jsx(Button, { variant: currentPanel === PanelView.Audio ? "primary" : "ghost", size: "sm", alt: t('store.viewAudio'), onClick: () => setCurrentPanel(PanelView.Audio), children: "Audio" }), hasTranscript &&
                                        _jsx(Button, { variant: currentPanel === PanelView.Transcript ? "primary" : "ghost", size: "sm", alt: t('store.viewTranscript'), onClick: () => setCurrentPanel(PanelView.Transcript), children: "Transcript" }), _jsx(Button, { variant: currentPanel === PanelView.Text ? "primary" : "ghost", size: "sm", alt: t('store.viewText'), onClick: () => setCurrentPanel(PanelView.Text), children: "Text" }), isPdf &&
                                        _jsx(Button, { variant: currentPanel === PanelView.Pdf ? "primary" : "ghost", size: "sm", alt: t('store.viewPdf'), onClick: () => setCurrentPanel(PanelView.Pdf), children: "PDF" }), isPreviewableAsPdfDoc && (_jsx(Button, { variant: currentPanel === PanelView.Pdf ? "primary" : "ghost", size: "sm", alt: t('store.viewAsPdf'), onClick: () => {
                                            setCurrentPanel(PanelView.Pdf);
                                            if (!pdfRendition && !officePdfUrl && !officePdfConverting) {
                                                triggerOfficePdfConversion();
                                            }
                                        }, disabled: officePdfConverting, children: officePdfConverting ? _jsx(Spinner, { size: "sm" }) : "PDF" }))] }), _jsx(PdfActions, { object: object })] }), currentPanel === PanelView.Text && !showProcessingPanel && !isEditing && (_jsx(TextActions, { object: object, text: displayText, fullText: fullText, handleCopyContent: handleCopyContent, textContainerRef: textContainerRef, isEditing: isEditing, onToggleEdit: () => setIsEditing(true), canEdit: canEdit })), currentPanel === PanelView.Pdf && isPreviewableAsPdfDoc && (pdfRendition || officePdfUrl) && (_jsx(OfficePdfActions, { object: object, pdfRendition: pdfRendition, officePdfUrl: officePdfUrl }))] }), currentPanel === PanelView.Image && (_jsx("div", { className: getPanelVisibility(true), children: _jsx(ImagePanel, { object: object }) })), currentPanel === PanelView.Video && (_jsx("div", { className: getPanelVisibility(true), children: _jsx(VideoPanel, { object: object }) })), currentPanel === PanelView.Audio && (_jsx("div", { className: getPanelVisibility(true), children: _jsx(AudioPanel, { object: object }) })), hasTranscript && currentPanel === PanelView.Transcript && (_jsx("div", { className: getPanelVisibility(true), children: _jsx(TranscriptPanel, { object: object, handleCopyContent: handleCopyContent }) })), isPdf && keepPdfPreviewMounted && (_jsx("div", { className: getPanelVisibility(showPdfPreviewPanel), children: _jsx(PdfPreviewPanel, { object: object }) })), isPreviewableAsPdfDoc && keepPdfPreviewMounted && (_jsx("div", { className: getPanelVisibility(showPdfPreviewPanel), children: _jsx(OfficePdfPreviewPanel, { pdfRendition: pdfRendition, officePdfUrl: officePdfUrl, officePdfConverting: officePdfConverting, officePdfError: officePdfError, onConvert: triggerOfficePdfConversion }) })), showPdfProcessingPanel && (_jsx("div", { className: getPanelVisibility(true), children: _jsx(PdfProcessingPanel, { progress: pdfProgress, status: pdfStatus, outputFormat: pdfOutputFormat }) })), currentPanel === PanelView.Text && !showProcessingPanel && !isEditing && isLoadingText && (_jsx("div", { className: getPanelVisibility(true), children: _jsx("div", { className: "flex justify-center items-center flex-1", children: _jsx(Spinner, { size: "lg" }) }) })), currentPanel === PanelView.Text && !showProcessingPanel && !isEditing && !isLoadingText && (_jsx("div", { className: getPanelVisibility(true), children: _jsx(TextPanel, { object: object, text: displayText, isTextCropped: isTextCropped, textContainerRef: textContainerRef }) })), isEditing && currentPanel === PanelView.Text && fullText != null && (_jsx(TextEditorPanel, { object: object, text: fullText, onClose: () => setIsEditing(false), onSaved: () => {
                    setIsEditing(false);
                    reloadText();
                    refetch?.();
                } }))] }));
}
function TextActions({ object, text, fullText, handleCopyContent, onToggleEdit, canEdit, }) {
    const { client, project } = useUserSession();
    const toast = useToast();
    const { t } = useUITranslation();
    const content = object.content;
    const { renderDocument, isDownloading } = useDownloadFile({ client, toast });
    const { data: fullProject } = useFetch(() => project ? client.projects.retrieve(project.id) : Promise.resolve(undefined), [project?.id]);
    const pdfTemplateObjectId = fullProject?.configuration?.pdf_template_object_id;
    const isMarkdown = content &&
        content.type &&
        content.type === "text/markdown";
    // Get content processor type for file extension detection
    const contentProcessorType = getContentProcessorType(object);
    const handleExportDocument = async (format, useDefaultTemplate) => {
        // Prevent multiple concurrent exports
        if (isDownloading)
            return;
        // Show immediate feedback
        toast({
            status: "info",
            title: `Preparing ${format.toUpperCase()}`,
            description: t('store.renderingDocument'),
            duration: 2000,
        });
        // For branded exports, use the project-configured template if available
        const templateObjectId = useDefaultTemplate !== false ? pdfTemplateObjectId : undefined;
        await renderDocument(object.id, {
            format,
            title: object.name || "document",
            useDefaultTemplate,
            templateObjectId,
        });
    };
    const handleExportDocx = () => handleExportDocument(MarkdownRenditionFormat.docx);
    const handleExportPdf = () => handleExportDocument(MarkdownRenditionFormat.pdf, false);
    const handleExportBrandedPdf = () => handleExportDocument(MarkdownRenditionFormat.pdf);
    const handleDownloadText = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!fullText)
            return;
        // Determine file extension based on content processor type
        let ext = "txt";
        let mimeType = "text/plain";
        if (contentProcessorType === "xml") {
            ext = "xml";
            mimeType = "text/xml";
        }
        else if (contentProcessorType === "markdown" || isMarkdown) {
            ext = "md";
            mimeType = "text/markdown";
        }
        const blob = new Blob([fullText], { type: mimeType });
        const url = URL.createObjectURL(blob);
        const filename = `${object.name || "document"}.${ext}`;
        // Use the download attribute with an anchor, but avoid triggering navigation
        const link = document.createElement("a");
        link.href = url;
        link.download = filename;
        link.style.display = "none";
        // Temporarily remove from DOM event flow
        setTimeout(() => {
            link.click();
            URL.revokeObjectURL(url);
        }, 0);
    };
    return (_jsx(_Fragment, { children: _jsx("div", { className: "h-[41px] text-lg font-semibold flex justify-between items-center px-2", children: _jsxs("div", { className: "flex items-center gap-2", children: [fullText && (_jsxs(_Fragment, { children: [_jsx(Button, { variant: "ghost", size: "sm", title: "Copy text", onClick: () => handleCopyContent(fullText, "text"), children: _jsx(Copy, { className: "size-4" }) }), canEdit && onToggleEdit && (_jsx(SecureButton, { permission: Permission.content_write, variant: "ghost", size: "sm", onClick: onToggleEdit, title: t('store.editText'), className: "flex items-center gap-2", children: _jsx(SquarePen, { className: "size-4" }) }))] })), isDownloading ? (_jsx(Button, { variant: "ghost", size: "sm", disabled: true, className: "flex items-center gap-2", alt: "download", children: _jsx(Spinner, { size: "sm" }) })) : (_jsxs(Dropdown, { trigger: _jsx(Button, { variant: "ghost", size: "sm", disabled: !text, className: "flex items-center gap-2", alt: "download", children: _jsx(Download, { className: "size-4" }) }), children: [fullText && (_jsx(MenuItem, { onClick: handleDownloadText, children: _jsxs("div", { className: "flex items-center gap-2", children: [_jsx(Download, { className: "size-4" }), "Download Text"] }) })), isMarkdown && text && (_jsxs(_Fragment, { children: [_jsx(MenuItem, { onClick: handleExportDocx, children: _jsxs("div", { className: "flex items-center gap-2", children: [_jsx(Download, { className: "size-4" }), "Export as DOCX"] }) }), _jsx(MenuItem, { onClick: handleExportPdf, children: _jsxs("div", { className: "flex items-center gap-2", children: [_jsx(Download, { className: "size-4" }), "Export as PDF"] }) }), _jsx(MenuItem, { onClick: handleExportBrandedPdf, children: _jsxs("div", { className: "flex items-center gap-2", children: [_jsx(Download, { className: "size-4" }), "Export as Branded PDF"] }) })] }))] }))] }) }) }));
}
const TextPanel = memo(({ object, text, isTextCropped, textContainerRef, }) => {
    const { t } = useUITranslation();
    const content = object.content;
    const isCreatedOrProcessing = isCreatedOrProcessingStatus(object?.status);
    // Check content processor type for XML
    const contentProcessorType = getContentProcessorType(object);
    const isXml = contentProcessorType === "xml";
    // Check if content type is markdown or plain text
    const isMarkdownOrText = content &&
        content.type &&
        (content.type === "text/markdown" || content.type === "text/plain");
    // Render as markdown if it's markdown/text type OR if text looks like markdown (but not if XML)
    const shouldRenderAsMarkdown = !isXml && (isMarkdownOrText || looksLikeMarkdown(text));
    return (text ? (_jsxs(_Fragment, { children: [isTextCropped && (_jsx("div", { className: "px-2 py-2 bg-attention/10 border-l-4 border-attention mx-2 mb-2 rounded", children: _jsxs("div", { className: "flex items-center gap-2 text-attention", children: [_jsx(AlertTriangle, { className: "size-4" }), _jsx("span", { className: "text-sm font-semibold", children: t('store.showingFirst128K') })] }) })), _jsx("div", { className: `max-w-7xl px-2 h-full overflow-auto`, ref: textContainerRef, children: isXml ? (_jsx("div", { className: "px-4 py-2", children: _jsx(XMLViewer, { xml: text, collapsible: true }) })) : shouldRenderAsMarkdown ? (_jsx("div", { className: "vprose prose-sm p-1", children: _jsx(MarkdownRenderer, { components: createMarkdownComponents(), children: text }) })) : (_jsx("pre", { className: "text-wrap bg-muted text-muted p-2", children: text })) })] })) :
        _jsx("div", { className: "px-2", children: _jsx("div", { children: isCreatedOrProcessing ? "Extracting content..." : "No content" }) }));
});
function ImagePanel({ object }) {
    const { client } = useUserSession();
    const [imageUrl, setImageUrl] = useState();
    const content = object.content;
    const isImage = object.metadata && object.metadata.type === ContentNature.Image;
    useEffect(() => {
        if (isImage) {
            // Reset image URL when object changes
            setImageUrl(undefined);
            const loadImage = async () => {
                const isOriginalWebSupported = content?.type && WEB_SUPPORTED_IMAGE_FORMATS.includes(content.type);
                try {
                    const rendition = await client.objects.getRendition(object.id, {
                        format: ImageRenditionFormat.jpeg,
                        generate_if_missing: false,
                        sign_url: true,
                    });
                    if (rendition.status === "found" && rendition.renditions?.length) {
                        // Use rendition URL directly
                        setImageUrl(rendition.renditions[0]);
                    }
                    else if (isOriginalWebSupported) {
                        // Fall back to original file only if web-supported
                        const downloadUrl = await client.files.getDownloadUrl(object.content.source);
                        setImageUrl(downloadUrl.url);
                    }
                }
                catch (error) {
                    // Fall back to original file only if web-supported
                    if (isOriginalWebSupported) {
                        const downloadUrl = await client.files.getDownloadUrl(object.content.source);
                        setImageUrl(downloadUrl.url);
                    }
                }
            };
            loadImage();
        }
    }, [object.id, isImage, content?.type, content?.source, client]);
    return (_jsx("div", { className: "mb-4 px-2", children: imageUrl ? (_jsx("img", { src: imageUrl, alt: object.name, className: "w-full object-contain" })) : (_jsx(Spinner, { size: "md" })) }));
}
function VideoPanel({ object }) {
    const { t } = useUITranslation();
    const { client } = useUserSession();
    const [videoUrl, setVideoUrl] = useState();
    const [posterUrl, setPosterUrl] = useState();
    const [isLoading, setIsLoading] = useState(true);
    const content = object.content;
    const isVideo = object.metadata?.type === ContentNature.Video;
    // Check if there are mp4 or webm renditions available in metadata
    const metadata = object.metadata;
    const renditions = metadata?.renditions || [];
    // Find mp4 or webm rendition by mime type, preferring mp4
    const webRendition = renditions.find(r => r.content.type === 'video/mp4') ||
        renditions.find(r => r.content.type === 'video/webm');
    // Check if original file is web-compatible
    const isOriginalWebSupported = content?.type && WEB_SUPPORTED_VIDEO_FORMATS.includes(content.type);
    // Get poster
    const poster = renditions.find(r => r.name === POSTER_RENDITION_NAME);
    // Reset state when object changes
    useEffect(() => {
        setVideoUrl(undefined);
        setPosterUrl(undefined);
        setIsLoading(true);
    }, [object.id]);
    useEffect(() => {
        const loadPoster = async () => {
            if (poster?.content?.source) {
                try {
                    const response = await client.files.getDownloadUrl(poster.content.source);
                    setPosterUrl(response.url);
                }
                catch (error) {
                    console.error("Failed to load poster image", error);
                }
            }
        };
        loadPoster();
    }, [poster, client]);
    useEffect(() => {
        if (isVideo && (webRendition?.content?.source || isOriginalWebSupported)) {
            const loadVideoUrl = async () => {
                try {
                    let downloadUrl;
                    if (webRendition?.content?.source) {
                        // Use rendition if available
                        downloadUrl = await client.files.getDownloadUrl(webRendition.content.source);
                    }
                    else if (isOriginalWebSupported && content?.source) {
                        // Fall back to original file if web-supported
                        downloadUrl = await client.files.getDownloadUrl(content.source);
                    }
                    if (downloadUrl) {
                        setVideoUrl(downloadUrl.url);
                    }
                }
                catch (error) {
                    console.error("Failed to get video URL", error);
                }
                finally {
                    setIsLoading(false);
                }
            };
            loadVideoUrl();
        }
        else {
            setIsLoading(false);
        }
    }, [isVideo, webRendition, isOriginalWebSupported, content?.source, client]);
    return (_jsx("div", { className: "mb-4 px-2 w-full h-full", children: !webRendition && !isOriginalWebSupported ? (_jsx("div", { className: "flex justify-center items-center h-[400px] text-muted", children: _jsxs("div", { className: "text-center", children: [_jsx("p", { children: t('store.noVideoRendition') }), _jsx("p", { className: "text-sm mt-2", children: t('store.videoFormatRequired') })] }) })) : isLoading ? (_jsx("div", { className: "flex justify-center items-center h-[400px]", children: _jsx(Spinner, { size: "md" }) })) : videoUrl ? (_jsx("video", { src: videoUrl, poster: posterUrl, controls: true, className: `w-full h-full object-contain`, children: "Your browser does not support the video tag." })) : (_jsx("div", { className: "flex justify-center items-center h-[400px] text-muted", children: "Failed to load video" })) }));
}
function AudioPanel({ object }) {
    const { t } = useUITranslation();
    const { client } = useUserSession();
    const [audioUrl, setAudioUrl] = useState();
    const [isLoading, setIsLoading] = useState(true);
    const content = object.content;
    const isAudio = object.metadata?.type === ContentNature.Audio;
    // Check if there are audio renditions available in metadata
    const metadata = object.metadata;
    const renditions = metadata?.renditions || [];
    // Find audio rendition by name (AUDIO_RENDITION_NAME = "Audio")
    const audioRendition = renditions.find(r => r.name === AUDIO_RENDITION_NAME);
    // Check if original file is web-compatible
    const isOriginalWebSupported = content?.type && WEB_SUPPORTED_AUDIO_FORMATS.includes(content.type);
    // Reset state when object changes
    useEffect(() => {
        setAudioUrl(undefined);
        setIsLoading(true);
    }, [object.id]);
    useEffect(() => {
        if (isAudio && (audioRendition?.content?.source || isOriginalWebSupported)) {
            const loadAudioUrl = async () => {
                try {
                    let downloadUrl;
                    if (audioRendition?.content?.source) {
                        // Use rendition if available
                        downloadUrl = await client.files.getDownloadUrl(audioRendition.content.source);
                    }
                    else if (isOriginalWebSupported && content?.source) {
                        // Fall back to original file if web-supported
                        downloadUrl = await client.files.getDownloadUrl(content.source);
                    }
                    if (downloadUrl) {
                        setAudioUrl(downloadUrl.url);
                    }
                }
                catch (error) {
                    console.error("Failed to get audio URL", error);
                }
                finally {
                    setIsLoading(false);
                }
            };
            loadAudioUrl();
        }
        else {
            setIsLoading(false);
        }
    }, [isAudio, audioRendition, isOriginalWebSupported, content?.source, client]);
    return (_jsx("div", { className: "mb-4 px-2", children: !audioRendition && !isOriginalWebSupported ? (_jsx("div", { className: "flex justify-center items-center h-[200px] text-muted", children: _jsxs("div", { className: "text-center", children: [_jsx("p", { children: t('store.noAudioRendition') }), _jsx("p", { className: "text-sm mt-2", children: t('store.audioFormatRequired') })] }) })) : isLoading ? (_jsx("div", { className: "flex justify-center items-center h-[200px]", children: _jsx(Spinner, { size: "md" }) })) : audioUrl ? (_jsxs("div", { className: "flex flex-col items-center gap-4", children: [_jsx("audio", { src: audioUrl, controls: true, className: "w-full max-w-2xl", children: "Your browser does not support the audio tag." }), metadata?.duration && (_jsxs("div", { className: "text-sm text-muted", children: ["Duration: ", formatDuration(metadata.duration)] }))] })) : (_jsx("div", { className: "flex justify-center items-center h-[200px] text-muted", children: "Failed to load audio" })) }));
}
function formatDuration(seconds) {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    if (hours > 0) {
        return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
}
function TranscriptPanel({ object, handleCopyContent }) {
    const { t } = useUITranslation();
    const transcript = object.transcript;
    const transcriptText = transcript?.text;
    const segments = transcript?.segments;
    // Build full text from segments if text is not available
    const fullText = transcriptText || (segments ? segments.map(s => s.text).join(' ') : '');
    const formatTimestamp = (seconds) => {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const secs = Math.floor(seconds % 60);
        if (hours > 0) {
            return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
        }
        return `${minutes}:${secs.toString().padStart(2, '0')}`;
    };
    return (_jsxs("div", { className: "flex flex-col h-full", children: [_jsx("div", { className: "flex justify-end items-center px-2 mb-2", children: fullText && (_jsx(Button, { variant: "ghost", size: "sm", title: "Copy transcript", onClick: () => handleCopyContent(fullText, "text"), children: _jsx(Copy, { className: "size-4" }) })) }), _jsx("div", { className: `h-full} overflow-auto px-2`, children: segments && segments.length > 0 ? (_jsx("div", { className: "space-y-2", children: segments.map((segment, idx) => (_jsxs("div", { className: "flex gap-3 text-sm", children: [_jsxs("span", { className: "text-muted font-mono text-xs shrink-0 pt-0.5", children: [formatTimestamp(segment.start), segment.end && ` - ${formatTimestamp(segment.end)}`] }), _jsx("span", { className: "flex-1", children: segment.text })] }, idx))) })) : transcriptText ? (_jsx("pre", { className: "text-wrap bg-muted text-muted p-2 whitespace-pre-wrap", children: transcriptText })) : (_jsx("div", { className: "text-muted", children: t('store.noTranscriptAvailable') })) })] }));
}
function PdfActions({ object }) {
    const [isPdfPreviewOpen, setPdfPreviewOpen] = useState(false);
    // Check if PDF has been processed (content_processor.type is xml or markdown)
    const contentProcessorType = getContentProcessorType(object);
    const hasPdfAnalysis = contentProcessorType === "xml" || contentProcessorType === "markdown";
    if (!hasPdfAnalysis)
        return null;
    return (_jsxs(_Fragment, { children: [_jsx(Button, { variant: "ghost", size: "sm", onClick: () => setPdfPreviewOpen(true), title: "Side by side view", children: _jsx(FileSearch, { className: "size-4" }) }), isPdfPreviewOpen && (_jsx(Portal, { children: _jsx(MagicPdfView, { objectId: object.id, onClose: () => setPdfPreviewOpen(false) }) }))] }));
}
function OfficePdfActions({ object, pdfRendition, officePdfUrl, }) {
    const { client } = useUserSession();
    const toast = useToast();
    const { t } = useUITranslation();
    const [isDownloading, setIsDownloading] = useState(false);
    const handleDownloadPdf = async () => {
        setIsDownloading(true);
        try {
            let downloadUrl = officePdfUrl;
            // If we have a rendition source but no signed URL yet, get a signed URL
            if (!downloadUrl && pdfRendition?.content?.source) {
                const response = await client.files.getDownloadUrl(pdfRendition.content.source, `${object.name || 'document'}.pdf`, 'attachment');
                downloadUrl = response.url;
            }
            if (downloadUrl) {
                // Open in new tab - browser will handle as download due to content-disposition
                window.open(downloadUrl, '_blank');
            }
        }
        catch (err) {
            console.error('Failed to download PDF:', err);
            toast({
                status: 'error',
                title: t('store.downloadFailed'),
                description: t('store.failedToDownloadPdf'),
                duration: 5000,
            });
        }
        finally {
            setIsDownloading(false);
        }
    };
    return (_jsx("div", { className: "flex items-center gap-2", children: _jsx(Button, { variant: "ghost", size: "sm", onClick: handleDownloadPdf, disabled: isDownloading, title: "Download PDF", children: isDownloading ? _jsx(Spinner, { size: "sm" }) : _jsx(Download, { className: "size-4" }) }) }));
}
function PdfPreviewPanel({ object }) {
    return (_jsx("div", { className: 'h-full', children: _jsx(SimplePdfViewer, { object: object, className: "h-full" }) }));
}
/**
 * Panel for displaying Office documents converted to PDF.
 * Handles the various states: converting, error, showing PDF.
 */
function OfficePdfPreviewPanel({ pdfRendition, officePdfUrl, officePdfConverting, officePdfError, onConvert, }) {
    const { t } = useUITranslation();
    if (officePdfConverting) {
        return (_jsxs("div", { className: "flex flex-col justify-center items-center flex-1 gap-2", children: [_jsx(Spinner, { size: "lg" }), _jsx("span", { className: "text-muted", children: t('store.convertingToPdf') })] }));
    }
    if (officePdfError) {
        return (_jsxs("div", { className: "flex flex-col justify-center items-center flex-1 gap-2 text-destructive", children: [_jsx(AlertTriangle, { className: "size-8" }), _jsx("span", { children: officePdfError })] }));
    }
    if (pdfRendition?.content?.source) {
        return (_jsx("div", { className: 'h-full', children: _jsx(SimplePdfViewer, { source: pdfRendition.content.source, className: "h-full" }) }));
    }
    if (officePdfUrl) {
        return (_jsx("div", { className: 'h-full', children: _jsx(SimplePdfViewer, { url: officePdfUrl, className: "h-full" }) }));
    }
    return (_jsx("div", { className: "flex flex-col justify-center items-center flex-1 gap-2", children: _jsx(Button, { onClick: onConvert, children: "Convert to PDF" }) }));
}
function PdfProcessingPanel({ progress, status, outputFormat }) {
    const { t } = useUITranslation();
    const statusColor = getWorkflowStatusColor(status);
    const statusName = getWorkflowStatusName(status);
    // Show detailed progress (tables, images, visuals) for XML processing
    const isXmlProcessing = outputFormat === "xml";
    // Ensure percent is a valid number (handle undefined and NaN from division by zero)
    const percent = progress?.percent != null && !isNaN(progress.percent) ? progress.percent : 0;
    return (_jsxs("div", { className: "px-4 py-4", children: [progress && (_jsxs("div", { className: "space-y-2", children: [_jsxs("div", { className: "flex flex-col gap-1", children: [_jsx(ProgressLine, { name: isXmlProcessing ? "Analyze Layouts" : "Analyze Page", progress: progress.pages }), isXmlProcessing && (_jsxs(_Fragment, { children: [_jsx(ProgressLine, { name: "Extract Tables", progress: progress.tables }), _jsx(ProgressLine, { name: "Describe Images", progress: progress.images }), _jsx(ProgressLine, { name: "Process Visually", progress: progress.visuals })] }))] }), _jsxs("div", { className: "pt-2 text-sm text-muted", children: ["Progress: ", percent, "%", _jsx("span", { className: "px-2", children: "\u2022" }), _jsx("span", { className: statusColor, children: statusName }), progress.started_at && (_jsxs(_Fragment, { children: [_jsx("span", { className: "px-2", children: "\u2022" }), _jsxs("span", { children: [((Date.now() - progress.started_at) / 1000).toFixed(0), " sec. elapsed"] })] }))] }), _jsx(Progress, { percent: percent })] })), !progress && (_jsxs("div", { className: "flex items-center gap-2 text-muted", children: [_jsx(Spinner, { size: "sm" }), _jsx("span", { children: t('store.loadingProcessingStatus') })] }))] }));
}
function ProgressLine({ name, progress }) {
    return (_jsxs("div", { className: "flex gap-2 text-sm", children: [_jsxs("span", { className: "text-muted min-w-36", children: [name, ":"] }), _jsxs("span", { children: [progress.processed, " of ", progress.total] })] }));
}
//# sourceMappingURL=ContentOverview.js.map